// Copyright 2021 Carnegie Mellon University. All Rights Reserved.
// Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.

import { FlatTreeControl } from '@angular/cdk/tree';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TaskDataService } from 'src/app/data/task/task-data.service';
import { TaskEditComponent } from 'src/app/components/tasks/task-edit/task-edit.component';
import {
  Result,
  Task,
  TaskAction,
  TaskIterationTermination,
  TaskStatus,
  TaskTrigger,
} from 'src/app/generated/steamfitter.api';
import { CrucibleDialogService } from '@cmusei/crucible-common';
interface TaskNode {
  task: Task;
  results?: Result[];
  children?: TaskNode[];
}

interface FlatTaskNode {
  expandable: boolean;
  level: number;
  task: Task;
  results?: Result[];
}

const BLANK_TASK: Task = {
  name: '',
  description: '',
  action: TaskAction.GuestProcessRun,
  vmMask: '',
  vmList: [],
  apiUrl: '',
  actionParameters: {},
  expectedOutput: '',
  delaySeconds: 0,
  expirationSeconds: 0,
  intervalSeconds: 0,
  iterations: 1,
  iterationTermination: TaskIterationTermination.IterationCount,
  triggerCondition: TaskTrigger.Manual,
  score: 0,
};

@Component({
    selector: 'app-task-tree',
    templateUrl: './task-tree.component.html',
    styleUrls: ['./task-tree.component.scss'],
    standalone: false
})
export class TaskTreeComponent implements OnInit, OnDestroy {
  @Input() taskList: Observable<Task[]>;
  @Input() resultList: Observable<Result[]>;
  @Input() scenarioTemplateId: string;
  @Input() scenarioId: string;
  @Input() userId: string;
  @Input() isEditableState: boolean;
  @Input() isLoading: Observable<boolean>;
  @Input() isExecutableState: boolean;
  @Input() clipboard: any;
  @Output() taskSelected = new EventEmitter<string>();
  @Output() saveTask = new EventEmitter<Task>();
  @Output() deleteTaskRequested = new EventEmitter<string>();
  @Output() executeRequested = new EventEmitter<string>();
  @Output() stopIterationsRequested = new EventEmitter<string>();
  @Output() sendToClipboard = new EventEmitter<any>();
  @Output() pasteClipboard = new EventEmitter<string>();
  @ViewChild(FlatTreeControl) flatTreeControl;
  selectedTaskId = '';
  manualTrigger = TaskTrigger.Manual;
  private tasks = new Array<Task>();
  private results = new Array<Result>();
  private expandedNodeSet = new Set<string>();
  expandedDetails = new Set<string>();
  private unsubscribe = new Subject();

  // tree controls
  private transformer = (node: TaskNode, level: number) => ({
    expandable: !!node.children && node.children.length > 0,
    level: level,
    task: node.task,
    results: node.results,
  });
  treeControl = new FlatTreeControl<FlatTaskNode>(
    (node) => node.level,
    (node) => node.expandable
  );
  treeFlattener = new MatTreeFlattener(
    this.transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  // context menu
  @ViewChild(MatMenuTrigger, { static: true }) contextMenu: MatMenuTrigger;
  contextMenuPosition = { x: '0px', y: '0px' };

  constructor(
    private confirmService: CrucibleDialogService,
    private dialog: MatDialog,
    private taskDataService: TaskDataService
  ) {}

  ngOnInit() {
    this.taskDataService.requestedTaskId$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((taskId) => {
        if (!!taskId && taskId !== this.selectedTaskId) {
          this.selectedTaskId = taskId;
        }
      });
    this.taskList.pipe(takeUntil(this.unsubscribe)).subscribe((tasks) => {
      this.tasks = !tasks ? [] : this.filterTasks(tasks);
      this.createTaskNodes();
      // Expand the task details panel if it matches the URL taskId
      if (this.selectedTaskId && this.tasks.some(t => t.id === this.selectedTaskId)) {
        this.expandedDetails.add(this.selectedTaskId);
      }
    });
    if (!!this.resultList) {
      this.resultList.pipe(takeUntil(this.unsubscribe)).subscribe((results) => {
        this.results = !results ? [] : results;
        this.createTaskNodes();
      });
    }
  }

  filterTasks(tasks: Task[]): Task[] {
    if (this.scenarioTemplateId) {
      tasks = tasks.filter(
        (item) =>
          item.scenarioTemplateId &&
          item.scenarioTemplateId === this.scenarioTemplateId
      );
    } else if (this.scenarioId) {
      tasks = tasks.filter(
        (item) => item.scenarioId && item.scenarioId === this.scenarioId
      );
    } else if (this.userId) {
      tasks = tasks.filter(
        (item) => item.userId && item.userId === this.userId
      );
    }

    return tasks;
  }

  hasChild = (_: number, node: FlatTaskNode) => node.expandable;

  /**
   * Creates TaskNodes from the tasks
   */
  private createTaskNodes() {
    const newTaskNodes = new Array<TaskNode>();
    this.tasks.forEach((task) => {
      if (!task.triggerTaskId) {
        const newNode = this.createTaskNode(task);
        newTaskNodes.push(newNode);
      }
    });
    this.rebuildTreeForData(newTaskNodes);
  }

  /**
   * Creates a TaskNode to display in the tree
   */
  private createTaskNode(parentTask: Task): TaskNode {
    const results = this.results.filter((result) => result.taskId === parentTask.id);
    const newNode: TaskNode = {
      task: parentTask,
      results: results,
      children: new Array<TaskNode>(),
    };
    newNode.task = parentTask;
    this.tasks.forEach((childTask) => {
      if (childTask.triggerTaskId === parentTask.id) {
        const newChildNode = this.createTaskNode(childTask);
        if (!newNode.children) {
          newNode.children = new Array<TaskNode>();
        }
        newNode.children.push(newChildNode);
      }
    });
    return newNode;
  }

  deselectTask(taskId: string) {
    this.expandedDetails.delete(taskId);
    if (this.selectedTaskId === taskId) {
      this.selectTask('');
    }
  }

  /**
   * Selects a Task to edit/create
   */
  selectTask(taskId: string) {
    this.selectedTaskId = taskId;
    this.taskSelected.emit(taskId);
  }

  /**
   * Determines if a Task can be edited
   */
  isEditableTask(taskId: string) {
    return this.isEditableState;
  }

  /**
   * Determines if a Task can be executed
   */
  isExecutableTask(task: Task) {
    // must be a manual scenario task
    let isExecutable = this.isExecutableState;
    // if this task has a parent, the parent task must be complete
    if (isExecutable) {
      const triggerTaskId = task.triggerTaskId;
      if (triggerTaskId) {
        const parent = this.tasks.find((dt) => dt.id === triggerTaskId);
        const results = this.results.filter((r) => r.taskId === triggerTaskId);
        if (results.length === 0) {
          isExecutable = false;
        } else if (
          results.some(
            (r) =>
              r.status === TaskStatus.Pending ||
              r.status === TaskStatus.Queued ||
              r.status === TaskStatus.Sent
          )
        ) {
          isExecutable = false;
        }
      }
    }
    return isExecutable;
  }

  onContextMenu(event: MouseEvent, task: Task) {
    event.preventDefault();
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';
    this.contextMenu.menuData = { item: task };
    this.contextMenu.menu.focusFirstItem('mouse');
    this.contextMenu.openMenu();
  }

  onContextEdit(task: Task) {
    this.selectedTaskId = task.id;
    this.taskSelected.emit(task.id);
    const dialogRef = this.dialog.open(TaskEditComponent, {
      data: { task: { ...task } },
      width: '50%',
      minWidth: '400px',
      maxWidth: '90vw',
    });
    dialogRef.componentInstance.editComplete.subscribe((result) => {
      if (result.saveChanges && result.task) {
        this.saveTask.emit(result.task);
      }
      dialogRef.close();
    });
  }

  onContextCopy(id: string) {
    this.sendToClipboard.emit({ id: id, resultId: undefined, isCut: false });
  }

  onContextCut(id: string) {
    this.sendToClipboard.emit({ id: id, resultId: undefined, isCut: true });
  }

  onContextPaste(id: string) {
    this.pasteClipboard.emit(id);
  }

  onContextNew(task: Task) {
    this.selectedTaskId = '';
    this.taskSelected.emit(this.selectedTaskId);
    // add a dependent Task
    const newTask = { ...BLANK_TASK };
    if (!!this.scenarioTemplateId) {
      newTask.scenarioTemplateId = this.scenarioTemplateId;
    }
    if (!!this.scenarioId) {
      newTask.scenarioId = this.scenarioId;
    }
    if (task && task.id) {
      newTask.triggerTaskId = task.id;
      newTask.triggerCondition = TaskTrigger.Completion;
    } else {
      newTask.triggerTaskId = null;
      newTask.triggerCondition = TaskTrigger.Time;
    }
    const dialogRef = this.dialog.open(TaskEditComponent, {
      data: { task: newTask },
      minWidth: '400px',
      maxWidth: '90vw',
    });
    dialogRef.componentInstance.editComplete.subscribe((result) => {
      if (result.saveChanges && result.task) {
        this.saveTask.emit(result.task);
      }
      dialogRef.close();
    });
  }

  onContextDelete(task: Task) {
    this.confirmService
      .confirm({
        title: 'Delete Task',
        message: 'Are you sure that you want to delete ' + task.name + '?',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.deleteTaskRequested.emit(task.id);
        }
      });
  }

  onContextExecute(task: Task) {
    this.confirmService
      .confirm({
        title: 'Execute Task',
        message: 'Are you sure that you want to execute ' + task.name + '?',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.executeRequested.emit(task.id);
        }
      });
  }

  onContextStopIterations(task: Task) {
    this.confirmService
      .confirm({
        title: 'Stop Task Iterations',
        message: 'Are you sure that you want to stop ' + task.name + '?',
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.stopIterationsRequested.emit(task.id);
        }
      });
  }

  /**
   * This constructs an array of nodes that matches the DOM,
   * and calls rememberExpandedTreeNodes to persist expand state
   */
  visibleNodes(): TaskNode[] {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    const result = [];

    function addExpandedChildren(node: TaskNode, expanded: Set<string>) {
      result.push(node);
      if (expanded.has(node.task.id)) {
        node.children.map((child) => addExpandedChildren(child, expanded));
      }
    }
    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expandedNodeSet);
    });
    return result;
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */

  rebuildTreeForData(data: any) {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = data;
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(
      this.treeControl.dataNodes,
      Array.from(this.expandedNodeSet)
    );
  }

  private rememberExpandedTreeNodes(
    treeControl: FlatTreeControl<FlatTaskNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.task.id);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(
    treeControl: FlatTreeControl<FlatTaskNode>,
    expandedNodeSet: Set<string>
  ) {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.task.id === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: FlatTaskNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) {
      return;
    }
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.task.id)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FlatTaskNode): FlatTaskNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  private static readonly STATUS_ICON_MAP: Record<string, string> = {
    succeeded: 'mdi-star-circle-outline',
    success: 'mdi-star-circle-outline',
    failed: 'mdi-close-circle-outline',
    failure: 'mdi-close-circle-outline',
    pending: 'mdi-z-wave',
    expired: 'mdi-clock-alert-outline',
    expiration: 'mdi-clock-alert-outline',
    error: 'mdi-alert-outline',
    completion: 'mdi-check-circle-outline',
    manual: 'mdi-gesture-tap-button',
    queued: 'mdi-clock-time-three-outline',
    sent: 'mdi-send',
    time: 'mdi-alarm',
    cancelled: 'mdi-cancel',
  };

  statusIcon(status: string): string {
    return TaskTreeComponent.STATUS_ICON_MAP[status?.toLowerCase()] || 'mdi-help-circle-outline';
  }

  sortedResults(results: Result[], sortBy: string, sortDescending: boolean) {
    const sortValue = sortDescending ? 1 : -1;
    const sortedResults = results.sort((a, b) =>
      a[sortBy] < b[sortBy] ? sortValue : -1 * sortValue
    );
    return sortedResults;
  }

  areDetailsExpanded(node: FlatTaskNode) {
    const id = !!node.task ? node.task.id : null;
    const isExpanded = !!id && this.expandedDetails.has(id);
    return isExpanded;
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }
}
